// 카리아카 헤더 포크(64px · 연 매출 상자 · 청구서 작성 버튼 · 프로필).
// 클래스 선택·카리아카톡·알림·웹소켓은 카리페이에 없어 뺐다. 프로필 버튼은 설정/로그아웃 메뉴를 연다.
import React, { useContext, useEffect, useRef, useState } from "react";
import { Page_settings } from "@/config/page_settings";
import colors from "@/design/colors";
import styled from "styled-components";
import fonts from "@/design/typography";
import { rootStore } from "@/mobx/store";
import { useNavigate } from "react-router-dom";
import { getFilePath } from "@/utils/image";
import ImageCircle from "@/components/image_circle";
import { observer } from "mobx-react";
import IconSend from "@/components/icons/send";
import { formatCommaNumber } from "@/utils/format";
import moment from "moment";
import BillTemplateCreateTypeModal from "@/pages/corp/payment_management/bill_template/modal/BillTemplateCreateTypeModal";

/* ----------------------------------
 * COMPONENT
 * 컴포넌트 정의
 ---------------------------------- */
interface HeaderProps {
  navigationOpen?: boolean;
  onToggleNavigation?: () => void;
}

const Header = ({ navigationOpen = false, onToggleNavigation }: HeaderProps) => {
  /* ----------------------------------
   * GLOBAL STATE
   * 전역 상태 및 전역 변수
   ---------------------------------- */
  const context: any = useContext(Page_settings);
  const userType = rootStore.getProfile?.userType;
  const token = rootStore.token;
  const navigate = useNavigate();

  const env = process.env.NODE_ENV;

  /* ----------------------------------
   * LOCAL STATE
   * 컴포넌트 내부에서 사용하는 상태
   ---------------------------------- */
  const [corpSales, setCorpSales] = useState<number>(0);
  const [showBillSendTypeModal, setShowBillSendTypeModal] =
    useState<boolean>(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState<boolean>(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const memberInfo = rootStore.getProfile;

  /* ----------------------------------
   * HANDLERS
   * 사용자 입력 및 이벤트 처리
   ---------------------------------- */
  // 멤버 정보 불러오기(포인트 잔액·사업자 등록 여부가 여기 있다)
  const getMemberInfo = () => {
    context.get("/member/info", {}, (response) => {
      rootStore.setProfile(response); // 전역 상태도 업데이트
    });
  };

  const getCorpSales = (onDone?: () => void) => {
    context.get(
      "/sales/year",
      { year: moment().format("YYYY") },
      (response) => {
        setCorpSales(response);
        onDone?.();
      },
    );
  };

  // 로그아웃 — 서버 세션을 끊지 못해도 이 브라우저에서는 로그아웃한다.
  const onLogout = () => {
    const refreshToken = localStorage.getItem("refresh_token");
    const done = () => {
      rootStore.signOut();
      navigate("/login");
    };
    setIsProfileMenuOpen(false);
    if (!refreshToken) {
      done();
      return;
    }
    context.post("auth/logout", { refreshToken }, done, done);
  };

  /* ----------------------------------
   * USE EFFECT
   * 컴포넌트 렌더링 시 실행되는 함수
   ---------------------------------- */
  // 프로필 메뉴 외부 클릭 시 닫기
  useEffect(() => {
    if (!isProfileMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!profileMenuRef.current?.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isProfileMenuOpen]);

  // 멤버 정보·매출 불러오기
  useEffect(() => {
    if (!token) {
      return;
    }
    getMemberInfo();
    getCorpSales();
  }, [token]);

  // 매출 갱신 이벤트 감지(청구서 발송·수납 기록 뒤)
  useEffect(() => {
    if (rootStore.shouldRefreshSales && token) {
      getCorpSales(() => rootStore.clearSalesRefresh()); // 갱신 완료 후 플래그 초기화
    }
  }, [rootStore.shouldRefreshSales, token]);

  return (
    <HeaderWrapper className="workspace-header">
      <MobileMenuButton
        type="button"
        aria-label="전체 메뉴 열기"
        aria-expanded={navigationOpen}
        aria-controls="workspace-navigation"
        onClick={onToggleNavigation}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </MobileMenuButton>
      <SalesSummary>
        <span
          style={{ ...fonts.label3SemiBold, color: colors.gray600 }}
        >{`${moment().format("YYYY")} 매출`}</span>
        <span
          style={{ ...fonts.label3SemiBold, color: colors.blue800 }}
        >{`${formatCommaNumber(corpSales)} 원`}</span>
      </SalesSummary>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
          flexShrink: 0,
        }}
      >
        {env === "development" && <EnvBox>DEVELOPMENT</EnvBox>}
        <HeaderIconButton
          type="button"
          $primary
          aria-label="청구서 작성"
          title="청구서 작성"
          onClick={() => setShowBillSendTypeModal(true)}
        >
          <IconSend
            color={colors.white}
            background={colors.white}
            width="24px"
            height="24px"
          />
        </HeaderIconButton>
        <SettingBox>
          <div ref={profileMenuRef} style={{ position: "relative" }}>
            <HeaderIconButton
              type="button"
              aria-label="내 정보 및 설정"
              title="내 정보 및 설정"
              aria-expanded={isProfileMenuOpen}
              onClick={() => setIsProfileMenuOpen((open) => !open)}
            >
              <ImageCircle
                src={getFilePath(memberInfo?.profile, undefined, userType)}
                size={32}
              />
            </HeaderIconButton>
            {isProfileMenuOpen && (
              <DropDownBox
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  zIndex: 1000,
                  minWidth: 180,
                }}
              >
                <ProfileSummary>
                  <span style={{ ...fonts.label4SemiBold, color: colors.gray900 }}>
                    {memberInfo?.name ?? "-"}
                  </span>
                  <span style={{ ...fonts.label5Medium, color: colors.gray500 }}>
                    {memberInfo?.email ?? ""}
                  </span>
                </ProfileSummary>
                <DropDownItem
                  onClick={() => {
                    setIsProfileMenuOpen(false);
                    navigate("/settings/business");
                  }}
                >
                  사업장 정보
                </DropDownItem>
                <DropDownItem
                  onClick={() => {
                    setIsProfileMenuOpen(false);
                    navigate("/settings/account");
                  }}
                >
                  계정
                </DropDownItem>
                <DropDownItem onClick={onLogout}>로그아웃</DropDownItem>
              </DropDownBox>
            )}
          </div>
        </SettingBox>
      </div>

      {showBillSendTypeModal && (
        <BillTemplateCreateTypeModal
          onClose={() => setShowBillSendTypeModal(false)}
          onClickSame={() => {
            setShowBillSendTypeModal(false);
            navigate("/payment/bill-send");
          }}
          onClickIndividual={() => {
            setShowBillSendTypeModal(false);
            navigate("/payment/bill-individual-send");
          }}
          sameImageSrc={"/assets/image/BillTemplateSame.svg"}
          individualImageSrc={"/assets/image/BillTemplateIndividual.svg"}
          title="청구서 작성 기준을 선택해 주세요"
        />
      )}
    </HeaderWrapper>
  );
};

const HeaderWrapper = styled.div`
  width: 100%;
  min-width: 0;
  min-height: 64px;
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 8px var(--cari-page-gutter, 24px);
  gap: 12px;
  background-color: ${colors.white};
  z-index: 5;
  position: relative;
  border-bottom: 1px solid ${colors.gray100};

  @media (max-width: 767px) {
    min-height: 60px;
    padding: 8px 12px;
    gap: 6px;
  }
`;

const HeaderIconButton = styled.button<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid ${({ $primary }) => $primary ? colors.blue600 : "transparent"};
  border-radius: 10px;
  background: ${({ $primary }) => $primary ? colors.blue600 : colors.white};
  color: ${({ $primary }) => $primary ? colors.white : colors.gray600};
  cursor: pointer;
  transition: background-color 150ms ease;

  &:hover { background: ${({ $primary }) => $primary ? colors.blue700 : colors.gray50}; }
  &:focus-visible { outline: 2px solid ${colors.blue600}; outline-offset: 2px; }

  @media (max-width: 767px) { width: 36px; height: 40px; flex-basis: 36px; }
`;

const MobileMenuButton = styled(HeaderIconButton)`
  display: none;
  @media (max-width: 1000px) { display: inline-flex; }
`;

const SalesSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 240px;
  min-width: 0;
  min-height: 40px;
  padding: 8px 12px;
  border-radius: 10px;
  background: ${colors.gray50};

  @media (max-width: 767px) {
    flex: 1;
    width: auto;
    gap: 2px;
    padding: 0;
    align-items: flex-start;
    justify-content: center;
    flex-direction: column;
    background: transparent;
    span { font-size: 11px; line-height: 16px; white-space: nowrap; }
    span:last-child { font-size: 13px; }
  }
`;

const SettingBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: ${colors.white};
  border-radius: 8px;
  padding: 0;
  height: 40px;
  gap: 4px;
  margin-left: 8px;
  @media (max-width: 767px) { gap: 0; margin-left: 2px; }
`;

const DropDownBox = styled.div`
  background-color: white;
  border: 1px solid ${colors.gray100};
  border-radius: 8px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
  padding: 4px 0;
`;

const ProfileSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 14px 10px;
  margin-bottom: 4px;
  border-bottom: 1px solid ${colors.gray100};
`;

const DropDownItem = styled.div`
  width: 100%;
  height: 34px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  color: ${colors.gray500};
  font-size: ${fonts.body3Regular.fontSize};
  line-height: ${fonts.body3Regular.lineHeight};
  font-family: ${fonts.body3Regular.fontFamily};
  &:hover {
    background-color: ${colors.blue50};
  }
`;

const EnvBox = styled.span`
  color: ${colors.blue800};
  font-size: 10px;
  font-weight: 600;
  background: ${colors.blue50};
  padding: 4px 6px;
  border-radius: 6px;
  margin-right: 8px;
  @media (max-width: 767px) { display: none; }
`;

export default observer(Header);
